import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Badge, Spinner, Flex, Icon, Image,
  SimpleGrid, Button, Select, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaSync, FaUser, FaMapMarkerAlt, FaHome, FaCog } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

// Build the resolved destination snapshot stored on each submission. For a
// drop-off spot it's the spot's own address; for a pickup it's the phleb's
// saved address ("company_address" on their profile).
const resolveDropLocation = (spot, phleb) => {
  if (!spot) return null;
  if (spot.kind === 'pickup') {
    const addr = phleb?.address ? ` — ${phleb.address}` : '';
    return `Home pickup (${spot.name}) — ${phleb?.name || 'phleb'}${addr}`;
  }
  return `${spot.name}${spot.address ? ` — ${spot.address}` : ''}`;
};

// Everything each phleb has spun + photographed and is holding to drop at the lab.
function ReadyForDrop() {
  const [rows, setRows] = useState([]);
  const [phlebs, setPhlebs] = useState({});   // id -> { name, address }
  const [spots, setSpots] = useState([]);      // active drop spots
  const [loading, setLoading] = useState(true);
  const [assignSel, setAssignSel] = useState({}); // phlebId -> chosen spot id (string)
  const [assigning, setAssigning] = useState(null); // phlebId currently saving
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, phlebsRes, spotsRes] = await Promise.all([
        supabase
          .from('submissions')
          .select('id, patient_name, patient_dob, lab_order_number, draw_code, phlebotomist_id, phlebotomist_name, spin_photo_url, ready_for_drop_at, drop_spot_id, drop_location')
          .in('outcome_code', ['spun', 'transit_to_dropoff'])
          .order('ready_for_drop_at', { ascending: true, nullsFirst: false }),
        supabase.from('phlebotomist_profiles').select('id, full_name, company_name, company_address, email'),
        supabase.from('drop_spots').select('id, name, kind, address, active').eq('active', true).order('name'),
      ]);
      if (subsRes.error) throw subsRes.error;
      setRows(subsRes.data || []);
      setPhlebs(Object.fromEntries((phlebsRes.data || []).map((p) => [
        p.id,
        { name: p.full_name || p.company_name || p.email || p.id, address: p.company_address || '' },
      ])));
      setSpots(spotsRes.data || []);
    } catch (err) {
      console.error('Ready-for-drop fetch error:', err);
      toast({ title: 'Could not load', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Group by phleb. Resolve the name from the profiles table (the submissions
  // row's phlebotomist_name isn't always populated), then fall back to the
  // row's stored name, then to Unassigned only when there's truly no phleb id.
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.phlebotomist_id || 'unassigned';
      if (!map.has(key)) {
        const name = r.phlebotomist_id
          ? (phlebs[r.phlebotomist_id]?.name || r.phlebotomist_name || 'Unknown phleb')
          : 'Unassigned';
        map.set(key, { phlebId: r.phlebotomist_id || null, name, items: [] });
      }
      map.get(key).items.push(r);
    }
    // Current assignment for the batch: the drop_spot_id shared by its items
    // (null if the batch is unassigned or the items disagree — i.e. "mixed").
    const out = [...map.values()];
    for (const g of out) {
      const ids = g.items.map((r) => r.drop_spot_id ?? null);
      const allSame = ids.every((v) => v === ids[0]);
      g.currentSpotId = allSame ? ids[0] : null;
      g.mixed = !allSame;
      g.currentLocation = allSame && ids[0] ? (g.items.find((r) => r.drop_location)?.drop_location || null) : null;
    }
    return out.sort((a, b) => b.items.length - a.items.length);
  }, [rows, phlebs]);

  // Assign / reassign the chosen drop spot to every specimen in this batch.
  const assignSpot = async (group) => {
    const key = group.phlebId || 'unassigned';
    const spotId = assignSel[key] ?? (group.currentSpotId ? String(group.currentSpotId) : '');
    if (!spotId) { toast({ title: 'Pick a drop spot first', status: 'warning', duration: 2000 }); return; }
    const spot = spots.find((s) => String(s.id) === String(spotId));
    if (!spot) return;
    const phleb = group.phlebId ? phlebs[group.phlebId] : null;
    const dropLocation = resolveDropLocation(spot, phleb);
    const ids = group.items.map((r) => r.id);
    setAssigning(key);
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ drop_spot_id: spot.id, drop_location: dropLocation })
        .in('id', ids);
      if (error) throw error;
      setRows((prev) => prev.map((r) => (ids.includes(r.id) ? { ...r, drop_spot_id: spot.id, drop_location: dropLocation } : r)));
      setAssignSel((prev) => ({ ...prev, [key]: String(spot.id) }));
      toast({ title: `Assigned ${ids.length} to ${spot.name}`, status: 'success', duration: 2500 });
    } catch (err) {
      toast({ title: 'Could not assign', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setAssigning(null);
    }
  };

  // Clear the drop spot from every specimen in this batch.
  const unassignSpot = async (group) => {
    const key = group.phlebId || 'unassigned';
    const ids = group.items.map((r) => r.id);
    setAssigning(key);
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ drop_spot_id: null, drop_location: null })
        .in('id', ids);
      if (error) throw error;
      setRows((prev) => prev.map((r) => (ids.includes(r.id) ? { ...r, drop_spot_id: null, drop_location: null } : r)));
      setAssignSel((prev) => ({ ...prev, [key]: '' }));
      toast({ title: 'Drop spot cleared', status: 'info', duration: 2000 });
    } catch (err) {
      toast({ title: 'Could not clear', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setAssigning(null);
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
        <Box>
          <Heading size="lg" mb={1}>Ready for Drop</Heading>
          <Text color={mutedColor} fontSize="sm">
            Specimens each phleb has spun and photographed, waiting to be dropped at the lab.
          </Text>
        </Box>
        <HStack>
          <Button as={RouterLink} to="/admin/drop-spots" size="sm" variant="outline" leftIcon={<Icon as={FaCog} />}>Manage spots</Button>
          <Button size="sm" variant="outline" leftIcon={<Icon as={FaSync} />} onClick={fetchData} isLoading={loading}>Refresh</Button>
        </HStack>
      </Flex>

      {loading ? (
        <Flex py={10} justify="center"><Spinner size="lg" /></Flex>
      ) : groups.length === 0 ? (
        <Text color={mutedColor}>Nothing ready for drop right now.</Text>
      ) : (
        <VStack align="stretch" spacing={5}>
          {groups.map((g) => {
            const key = g.phlebId || 'unassigned';
            const homeAddr = g.phlebId ? phlebs[g.phlebId]?.address : '';
            // Dropdown reflects the batch's current assignment until the admin
            // changes it (assignSel overrides once they touch it).
            const selValue = assignSel[key] !== undefined ? assignSel[key] : (g.currentSpotId ? String(g.currentSpotId) : '');
            const selectedSpot = spots.find((s) => String(s.id) === String(selValue));
            const isAssigned = !!g.currentSpotId;
            const changed = String(selValue) !== String(g.currentSpotId ?? '');
            return (
              <Box key={key} borderWidth="1px" borderColor={borderColor} borderRadius="xl" bg={cardBg} p={4} shadow="sm">
                <Flex mb={3} align="flex-start" justify="space-between" flexWrap="wrap" gap={3}>
                  <Box minW={0}>
                    <HStack>
                      <Icon as={FaUser} color="gray.400" />
                      <Heading size="sm">{g.name}</Heading>
                      <Badge colorScheme="cyan" borderRadius="full">{g.items.length} in batch</Badge>
                    </HStack>
                    {/* This batch's own phleb home address — used when a home-pickup spot is chosen. */}
                    {homeAddr ? (
                      <HStack spacing={1.5} mt={1} color={mutedColor}>
                        <Icon as={FaHome} boxSize={3} />
                        <Text fontSize="xs" noOfLines={1}>Home: {homeAddr}</Text>
                      </HStack>
                    ) : g.phlebId && (
                      <Text fontSize="xs" color="orange.500" mt={1}>No home address on file — set it on the phleb profile for pickups.</Text>
                    )}
                  </Box>
                  <VStack align="flex-end" spacing={1}>
                    <HStack>
                      <Select
                        size="sm" w="220px" placeholder={spots.length ? 'Choose a drop spot…' : 'No spots — add one first'}
                        isDisabled={!spots.length}
                        value={selValue}
                        onChange={(e) => setAssignSel((prev) => ({ ...prev, [key]: e.target.value }))}
                      >
                        {spots.map((s) => (
                          <option key={s.id} value={s.id}>{s.kind === 'pickup' ? '🏠 ' : '📍 '}{s.name}</option>
                        ))}
                      </Select>
                      <Button size="sm" colorScheme="blue" onClick={() => assignSpot(g)}
                        isLoading={assigning === key} isDisabled={!selValue || (isAssigned && !changed)}>
                        {isAssigned ? (changed ? 'Change spot' : 'Assigned') : 'Assign this batch'}
                      </Button>
                      {isAssigned && (
                        <Button size="sm" variant="ghost" colorScheme="red" onClick={() => unassignSpot(g)} isLoading={assigning === key}>
                          Clear
                        </Button>
                      )}
                    </HStack>
                    {/* Current assignment (green) or the pending change preview (grey). */}
                    {g.mixed ? (
                      <Text fontSize="2xs" color="orange.500" textAlign="right">Mixed — specimens have different drop spots. Pick one to align them.</Text>
                    ) : isAssigned && !changed ? (
                      <Text fontSize="2xs" color="green.600" textAlign="right" maxW="320px" noOfLines={2}>✓ {g.currentLocation}</Text>
                    ) : selectedSpot ? (
                      <Text fontSize="2xs" color={mutedColor} textAlign="right" maxW="320px" noOfLines={2}>
                        → {resolveDropLocation(selectedSpot, g.phlebId ? phlebs[g.phlebId] : null)}
                      </Text>
                    ) : null}
                  </VStack>
                </Flex>
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={3}>
                  {g.items.map((r) => (
                    <Box key={r.id} bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" overflow="hidden" shadow="sm">
                      {r.spin_photo_url ? (
                        <Image src={r.spin_photo_url} alt="Spun specimen" w="100%" h="140px" objectFit="cover" />
                      ) : (
                        <Flex h="140px" bg="gray.50" align="center" justify="center"><Text fontSize="xs" color="gray.400">no photo</Text></Flex>
                      )}
                      <Box p={3}>
                        <Text fontWeight="700" noOfLines={1}>{r.patient_name || 'Unknown patient'}</Text>
                        <HStack spacing={2} mt={1} flexWrap="wrap">
                          {r.lab_order_number && <Badge colorScheme="blue" fontFamily="mono">#{r.lab_order_number}</Badge>}
                          <Text fontSize="xs" color={mutedColor}>DOB {r.patient_dob || '—'}</Text>
                        </HStack>
                        <Text fontSize="xs" color={mutedColor} mt={1}>Ready {fmtWhen(r.ready_for_drop_at)}</Text>
                        <HStack mt={2} spacing={1} align="flex-start">
                          <Icon as={r.drop_location ? FaMapMarkerAlt : FaHome} color={r.drop_location ? 'green.500' : 'gray.300'} mt="2px" flexShrink={0} boxSize={3} />
                          <Text fontSize="xs" color={r.drop_location ? 'green.700' : 'gray.400'} noOfLines={2}>
                            {r.drop_location || 'No drop spot assigned'}
                          </Text>
                        </HStack>
                      </Box>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}

export default ReadyForDrop;
